import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, TrendingUp, AlertCircle, ArrowRight, PieChart, FileText, Globe } from 'lucide-react';

const VisaProbabilityScoring: React.FC = () => {
    const navigate = useNavigate();

    const handleRunCheck = () => {
        navigate('/eligibility?service=immigration');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20 font-sans">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-[#011E26] via-[#011E26] to-[#013540] text-white py-24 mb-16 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
                    <img
                        src="https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?q=80&w=2070&auto=format&fit=crop"
                        alt="Data Analysis"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#009B8F]/20 rounded-full text-[#009B8F] text-xs font-bold uppercase tracking-widest mb-8 border border-[#009B8F]/30">
                        <TrendingUp size={14} /> Data-Driven Analysis
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-none">
                        Visa <span className="text-[#009B8F]">Probability</span> <br /> Scoring
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-300 max-w-3xl font-light leading-relaxed mb-10">
                        Stop relying on guesswork. Receive a personalised score based on measurable patterns,
                        historical trends, and global immigration criteria.
                    </p>
                    <button
                        onClick={handleRunCheck}
                        className="group bg-[#009B8F] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#011E26] transition-all flex items-center gap-3 shadow-2xl"
                    >
                        Run a Check with AI
                        <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                {/* Core Logic Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-32">
                    <div className="order-2 lg:order-1 relative">
                        <div className="absolute -inset-10 bg-[#009B8F]/5 rounded-full blur-[100px]"></div>
                        <img
                            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                            alt="Scoring Visual"
                            className="relative rounded-[3rem] shadow-2xl border border-slate-100"
                        />
                    </div>
                    <div className="order-1 lg:order-2 space-y-8">
                        <h2 className="text-4xl font-bold text-[#011E26] leading-tight">
                            A Smart Comparison <span className="text-[#009B8F]">Engine</span>.
                        </h2>
                        <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                            <p>
                                The Visa Probability Scoring tool gives you a clear, data‐driven understanding of how
                                closely your profile aligns with successful visa applications.
                            </p>
                            <p>
                                Your details are analysed against thousands of known success patterns. The result is a
                                percentage‐based likelihood score that gives you clarity and confidence from the very
                                beginning.
                            </p>
                        </div>
                        <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-2xl">
                            <div className="flex gap-4">
                                <AlertCircle className="text-amber-500 flex-shrink-0" />
                                <p className="text-sm text-amber-900 italic">
                                    This tool does not interpret immigration law and does not replace professional legal
                                    advice. It functions purely as a smart comparison engine.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Workflow Section */}
                <div className="mb-32">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-bold text-[#011E26] mb-4">How the AI‐Run Check Works</h2>
                        <p className="text-slate-500 text-lg">A structured, automated comparison to enhance your preparation.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { title: "Data Entry", desc: "You provide key details like visa type, financial maintenance, and travel records." },
                            { title: "Matching", desc: "System checks inputs against publicly available rules for your category." },
                            { title: "Pattern Scoring", desc: "Data is compared to historical success patterns for alignment." },
                            { title: "Risk flagging", desc: "Inconsistencies are flagged as general risk indicators, not legal problems." }
                        ].map((step, i) => (
                            <div key={i} className="p-8 bg-slate-50 rounded-[2rem] border border-slate-100 hover:border-[#009B8F] transition-all group">
                                <div className="text-5xl font-black text-[#011E26]/5 group-hover:text-[#009B8F]/10 mb-6 transition-colors">0{i + 1}</div>
                                <h3 className="text-xl font-bold text-[#011E26] mb-4">{step.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-[#011E26] rounded-[4rem] p-12 md:p-24 text-white shadow-2xl mb-32 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-[#009B8F]/10 rounded-full blur-[120px]"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-4xl font-bold mb-8">Safe, Compliant Output</h2>
                            <ul className="space-y-6">
                                {[
                                    { icon: <PieChart size={20} />, text: "Percentage likelihood score" },
                                    { icon: <FileText size={20} />, text: "Pattern-based success explanation" },
                                    { icon: <AlertCircle size={20} />, text: "List of general risk indicators" },
                                    { icon: <ShieldCheck size={20} />, text: "Automated AI risk disclaimer" }
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-4 group">
                                        <div className="p-3 bg-white/5 rounded-xl group-hover:bg-[#009B8F] transition-colors">
                                            {item.icon}
                                        </div>
                                        <span className="text-slate-300 group-hover:text-white transition-colors">{item.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="w-64 h-64 rounded-full border-[12px] border-[#009B8F]/20 flex items-center justify-center relative">
                                <div className="absolute inset-0 border-[12px] border-[#009B8F] rounded-full border-r-transparent animate-spin-slow"></div>
                                <div className="text-center">
                                    <span className="text-6xl font-black">84<span className="text-[#009B8F] text-2xl">%</span></span>
                                    <p className="text-xs uppercase tracking-widest text-[#009B8F] font-bold mt-2">Likelihood</p>
                                </div>
                            </div>
                            <p className="text-slate-400 text-xs mt-12 italic text-center max-w-xs">
                                *Indicator based on historical success patterns and standard criteria comparison.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="text-center border-t border-slate-100 pt-20">
                    <div className="inline-block p-4 bg-slate-50 rounded-2xl mb-8">
                        <Globe className="text-[#009B8F]" />
                    </div>
                    <h2 className="text-4xl font-bold text-[#011E26] mb-4">Ready to Run a Check?</h2>
                    <p className="text-slate-500 text-xl mb-12">No document uploads required. Fast, private, and simple.</p>
                    <button
                        onClick={handleRunCheck}
                        className="group bg-[#011E26] text-white px-12 py-6 rounded-full font-bold text-xl hover:bg-[#009B8F] transition-all flex items-center gap-3 mx-auto shadow-2xl"
                    >
                        Start Your Probability Scan
                        <ArrowRight className="group-hover:translate-x-2 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VisaProbabilityScoring;
